import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Switch,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  InputAdornment, 
  Chip,
  Stack,
  CssBaseline // <--- CRITICAL FIX FOR DARK BACKGROUNDS
} from "@mui/material";
import { ThemeProvider, styled, alpha, useTheme } from "@mui/material/styles";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import ClassIcon from "@mui/icons-material/Class";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import SearchIcon from "@mui/icons-material/Search";
import Lottie from "lottie-react";

import axiosInstance from "../../api/axiosConfig";
import adminTheme from "../../theme";
import PageContainer from "../common/PageContainer";
import AdminPageHero from "../common/AdminPageHero";
import AdminPanelCard from "../common/AdminPanelCard";
import animationData from "../../assets/icons/no-results.json";

// --- Styled Components ---

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  borderRadius: theme.spacing(2.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
  boxShadow: "none",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: alpha(theme.palette.divider, 0.6),
  },
}));

const SectionChip = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
  color: theme.palette.primary.main,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  padding: "4px 10px",
  borderRadius: "16px",
  fontSize: "0.75rem",
  fontWeight: 600,
  display: "inline-block",
}));

const FadeTransition = React.forwardRef(function FadeTransition(props, ref) {
  return <Fade ref={ref} {...props} />;
});

// --- Main Component ---

const ClassManagement = () => {
  const theme = useTheme();
  // Data States
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);

  // Dialog States
  const [openSectionsDialog, setOpenSectionsDialog] = useState(false);
  const [checkedSections, setCheckedSections] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editClassName, setEditClassName] = useState("");
  const [editRoomNo, setEditRoomNo] = useState("");
  const [newClassDialogOpen, setNewClassDialogOpen] = useState(false);
  const [newClassName, setNewClassName] = useState("");
  const [newRoomNo, setNewRoomNo] = useState("");
  const [assignTeacherDialogOpen, setAssignTeacherDialogOpen] = useState(false);
  const [classTeacherId, setClassTeacherId] = useState("");

  // UI/UX States
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Filters & Views
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [viewMode, setViewMode] = useState("list");

  const iSchoolId = localStorage.getItem("schoolId") || "1";

  // --- API Calls ---
  const fetchClasses = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/api/classes?schoolId=${iSchoolId}`);
      const classesWithSections = response.data.map((item) => ({
        ...item,
        assignedSections: item.assignedSections ? item.assignedSections.split(",").map(Number).filter(n => !isNaN(n)) : [],
      }));
      setClasses(classesWithSections);
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to fetch classes.", "error");
    }
  }, [iSchoolId]);

  const fetchSections = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/api/sections?schoolId=${iSchoolId}`);
      setSections(response.data);
    } catch (error) {
      showSnackbar("Failed to fetch sections.", "error");
    }
  }, [iSchoolId]);

  const fetchTeachers = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/api/employees?role=teacher`);
      setTeachers(response.data);
    } catch (error) {
      showSnackbar("Failed to fetch teachers.", "error");
    }
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      await Promise.all([fetchClasses(), fetchSections(), fetchTeachers()]);
      setIsLoading(false);
    };
    fetchAllData();
  }, [fetchClasses, fetchSections, fetchTeachers]);

  // --- Handlers ---
  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const handleStatusToggle = async (classId) => {
    setActionLoading(true);
    try {
      await axiosInstance.post(`/api/classes/status/${classId}`);
      await fetchClasses();
      showSnackbar("Class status updated");
    } catch (error) {
      showSnackbar("Failed to update status", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveSections = async () => {
    setActionLoading(true);
    try {
      const normalizedSections = [...new Set(checkedSections.map(Number).filter((id) => !Number.isNaN(id)))];
      await axiosInstance.patch(
        `/api/classes/${selectedClass.classId}/sections`,
        normalizedSections.join(","),
        { headers: { "Content-Type": "text/plain" } }
      );
      setClasses((prev) =>
        prev.map((item) =>
          item.classId === selectedClass.classId
            ? { ...item, assignedSections: normalizedSections }
            : item
        )
      );
      setSelectedClass((prev) =>
        prev ? { ...prev, assignedSections: normalizedSections } : prev
      );
      await fetchClasses();
      setOpenSectionsDialog(false);
      showSnackbar("Sections updated successfully");
    } catch (error) {
      showSnackbar("Failed to update sections", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveClassDetails = async () => {
    if (!editClassName.trim()) return showSnackbar("Class Name is required", "warning");
    setActionLoading(true);
    try {
      await axiosInstance.put(`/api/classes/${selectedClass.classId}`, { ...selectedClass, className: editClassName, roomNo: editRoomNo });
      await fetchClasses();
      setEditDialogOpen(false);
      showSnackbar("Class updated successfully");
    } catch (error) {
      showSnackbar("Failed to update class", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateNewClass = async () => {
    if (!newClassName.trim()) return showSnackbar("Class Name is required", "warning");
    setActionLoading(true);
    try {
      await axiosInstance.post("/api/classes", { schoolId: iSchoolId, className: newClassName, roomNo: newRoomNo, status: "Active" });
      await fetchClasses();
      setNewClassDialogOpen(false);
      setNewClassName("");
      setNewRoomNo("");
      showSnackbar("Class created successfully");
    } catch (error) {
      showSnackbar("Failed to create class", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignTeacher = async () => {
    setActionLoading(true);
    try {
      await axiosInstance.patch(`/api/classes/${selectedClass.classId}/teacher`, { classTeacherId });
      await fetchClasses();
      setAssignTeacherDialogOpen(false);
      showSnackbar("Teacher assigned successfully");
    } catch (error) {
      showSnackbar("Failed to assign teacher", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClass = async (classId) => {
    if (window.confirm("Are you sure you want to delete this class? This action cannot be undone.")) {
      setActionLoading(true);
      try {
        await axiosInstance.delete(`/api/classes/${classId}`);
        await fetchClasses();
        showSnackbar("Class deleted successfully");
      } catch (error) {
        showSnackbar("Failed to delete class", "error");
      } finally {
        setActionLoading(false);
      }
    }
  };

  // --- Helpers ---
  const getTeacherName = (id) => {
    const teacher = teachers.find((t) => t.employeeId === id);
    return teacher ? `${teacher.firstName} ${teacher.lastName}` : "Unassigned";
  };
  
  const getSectionName = (id) => {
    const section = sections.find((s) => s.sectionId === id);
    return section ? section.sectionName : `Removed section #${id}`;
  };

  const getSectionOptionsForClass = (classItem) => {
    const legacySectionIds = (classItem?.assignedSections || []).filter(
      (id) => !sections.some((section) => section.sectionId === id)
    );

    const legacyOptions = legacySectionIds.map((id) => ({
      sectionId: id,
      sectionName: `Removed section #${id}`,
      isLegacy: true,
    }));

    return [...sections, ...legacyOptions];
  };

  const filteredClasses = useMemo(() => {
    return classes.filter((c) => {
      const matchesSearch = c.className.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "All" || c.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [classes, searchQuery, statusFilter]);

  return (
    <ThemeProvider theme={adminTheme}>
      <CssBaseline /> {/* <--- KILLS THE BLACK BACKGROUND BUG GLOBALLY */}

      <PageContainer sx={{ width: '100%' }}>
        <AdminPageHero
          title="Class Management"
          subtitle="Create classes, assign reusable section masters, and manage class teachers in one place."
          metrics={[
            { label: "Classes", value: classes.length },
            { label: "Active", value: classes.filter((item) => item.status === "Active").length },
            { label: "Sections", value: sections.length },
            { label: "Teachers", value: teachers.length },
          ]}
          actions={(
            <Button
              variant="contained"
              color="primary"
              onClick={() => setNewClassDialogOpen(true)}
              startIcon={<ClassIcon />}
              disableElevation
              sx={{ py: 1, px: 2 }}
            >
              Add New Class
            </Button>
          )}
          sx={{ mb: 3 }}
        />

        <AdminPanelCard
          title="Class Directory"
          subtitle="Search classes, filter by status, and switch between grid and list views."
          sx={{ mb: 3 }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" gap={2} flexWrap="wrap" flexGrow={1} maxWidth={600}>
              <TextField
                placeholder="Search classes..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  flexGrow: 1,
                  minWidth: 220,
                  "& .MuiOutlinedInput-root": { bgcolor: "background.paper" }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                }}
              />

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ bgcolor: "background.paper", px: 0.5 }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{ bgcolor: "background.paper" }}
                >
                  <MenuItem value="All">All Statuses</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                "& .MuiToggleButton-root": {
                  border: 0,
                  px: 1.2,
                },
                "& .Mui-selected": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                }
              }}
            >
              <Tooltip title="Grid View">
                <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
              </Tooltip>
              <Tooltip title="List View">
                <ToggleButton value="list"><ViewListIcon /></ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
        </AdminPanelCard>

        {/* --- Main Content --- */}
        {isLoading ? (
          <AdminPanelCard title="Classes" subtitle="Loading class records and teacher assignments.">
            <Stack spacing={2}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    height: 64,
                    borderRadius: 1.5,
                    bgcolor: alpha("#94A3B8", 0.14),
                  }}
                />
              ))}
            </Stack>
          </AdminPanelCard>
        ) : filteredClasses.length > 0 ? (
          
          viewMode === "grid" ? (
            /* --- GRID VIEW --- */
            <Grid container spacing={3}>
              {filteredClasses.map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.classId}>
                  <StyledCard>
                    <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 3 }}>
                      
                      {/* Header */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Typography variant="h6" color="text.primary" noWrap title={item.className}>
                          {item.className}
                        </Typography>
                        <Tooltip title={`Mark as ${item.status === 'Active' ? 'Inactive' : 'Active'}`}>
                          <Switch
                            checked={item.status === "Active"}
                            onChange={() => handleStatusToggle(item.classId)}
                            color="success"
                            size="small"
                            disabled={actionLoading}
                            sx={{ mr: -1, mt: -0.5 }}
                          />
                        </Tooltip>
                      </Box>
                      
                      {/* Body */}
                      <Box display="flex" flexDirection="column" gap={1.5} mb={3} flexGrow={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Room</Typography>
                          <Typography variant="body2" fontWeight="600">{item.roomNo || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Teacher</Typography>
                          <Typography variant="body2" fontWeight="600">{getTeacherName(item.classTeacherId)}</Typography>
                        </Box>

                        <Box mt={1}>
                          <Typography variant="subtitle2" color="text.secondary" mb={1}>
                            Assigned Sections
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {item.assignedSections?.length > 0 ? (
                              item.assignedSections.map((id) => (
                                <SectionChip key={id}>{getSectionName(id)}</SectionChip>
                              ))
                            ) : (
                              <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                No sections assigned
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Actions Footer */}
                      <Box display="flex" justifyContent="flex-end" gap={0.5} pt={2} borderTop={1} borderColor="divider">
                        <Tooltip title="Edit Details"><IconButton size="small" onClick={() => { setSelectedClass(item); setEditClassName(item.className); setEditRoomNo(item.roomNo || ""); setEditDialogOpen(true); }}><EditIcon fontSize="small" color="action" /></IconButton></Tooltip>
                        <Tooltip title="Manage Sections"><IconButton size="small" onClick={() => { setSelectedClass(item); setCheckedSections(item.assignedSections || []); setOpenSectionsDialog(true); }}><GroupIcon fontSize="small" color="action" /></IconButton></Tooltip>
                        <Tooltip title="Assign Teacher"><IconButton size="small" onClick={() => { setSelectedClass(item); setClassTeacherId(item.classTeacherId || ""); setAssignTeacherDialogOpen(true); }}><PersonIcon fontSize="small" color="action" /></IconButton></Tooltip>
                        <Tooltip title="Delete Class"><IconButton size="small" onClick={() => handleDeleteClass(item.classId)}><DeleteIcon fontSize="small" color="error" /></IconButton></Tooltip>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          ) : (
            /* --- LIST VIEW --- */
            <AdminPanelCard
              title="Classes List"
              subtitle="Manage class details, sections, teacher mapping, and status from one table."
            >
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  overflow: "hidden",
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" variant="outlined" label={`Classes: ${filteredClasses.length}`} />
                    <Chip size="small" variant="outlined" label={`Active: ${filteredClasses.filter((c) => c.status === "Active").length}`} />
                  </Stack>
                </Box>
                <Table sx={{ minWidth: 860 }}>
                  <TableHead sx={{ bgcolor: "#F5F7FB" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: 56 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Class Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Room</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Teacher</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Sections</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "text.secondary" }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredClasses.map((item, index) => (
                      <TableRow
                        key={item.classId}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:nth-of-type(even)": { bgcolor: alpha("#0F172A", 0.02) },
                        }}
                      >
                        <TableCell sx={{ color: "text.secondary", fontWeight: 700 }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{item.className}</TableCell>
                        <TableCell>{item.roomNo || "-"}</TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={getTeacherName(item.classTeacherId)} />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {item.assignedSections?.length > 0
                              ? item.assignedSections.map(id => <SectionChip key={id}>{getSectionName(id)}</SectionChip>)
                              : <Typography variant="caption" color="text.secondary">None</Typography>
                            }
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.8} alignItems="center">
                            <Chip
                              size="small"
                              variant="outlined"
                              label={item.status || "Unknown"}
                              sx={{
                                minWidth: 70,
                                borderColor: item.status === "Active" ? alpha(theme.palette.success.main, 0.4) : alpha(theme.palette.divider, 0.8),
                                color: item.status === "Active" ? theme.palette.success.main : theme.palette.text.secondary,
                                backgroundColor: item.status === "Active" ? alpha(theme.palette.success.main, 0.1) : "transparent",
                              }}
                            />
                            <Switch
                              checked={item.status === "Active"}
                              onChange={() => handleStatusToggle(item.classId)}
                              color="success"
                              size="small"
                            />
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => { setSelectedClass(item); setEditClassName(item.className); setEditRoomNo(item.roomNo || ""); setEditDialogOpen(true); }}><EditIcon fontSize="small" color="action" /></IconButton></Tooltip>
                          <Tooltip title="Sections"><IconButton size="small" onClick={() => { setSelectedClass(item); setCheckedSections(item.assignedSections || []); setOpenSectionsDialog(true); }}><GroupIcon fontSize="small" color="action" /></IconButton></Tooltip>
                          <Tooltip title="Teacher"><IconButton size="small" onClick={() => { setSelectedClass(item); setClassTeacherId(item.classTeacherId || ""); setAssignTeacherDialogOpen(true); }}><PersonIcon fontSize="small" color="action" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDeleteClass(item.classId)}><DeleteIcon fontSize="small" color="error" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AdminPanelCard>
          )

        ) : (
          /* --- EMPTY STATE --- */
          <AdminPanelCard>
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
              <Box width={150} height={150} mb={2} opacity={0.8}>
                {animationData ? (
                   <Lottie animationData={animationData} loop autoplay style={{ height: "100%", width: "100%" }} />
                ) : (
                   <ClassIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
                )}
              </Box>
              <Typography variant="h6" color="text.primary" fontWeight="bold" gutterBottom>
                {searchQuery || statusFilter !== "All" ? "No matches found" : "No classes added yet"}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {searchQuery || statusFilter !== "All"
                  ? "Try adjusting your search or filter settings."
                  : "Get started by adding your first class to the system."}
              </Typography>
              {!(searchQuery || statusFilter !== "All") && (
                <Button variant="contained" onClick={() => setNewClassDialogOpen(true)} disableElevation>
                  Add First Class
                </Button>
              )}
            </Box>
          </AdminPanelCard>
        )}

        {/* --- DIALOGS --- */}
        
        {/* Manage Sections Dialog */}
        <Dialog open={openSectionsDialog} onClose={() => setOpenSectionsDialog(false)} maxWidth="xs" fullWidth TransitionComponent={FadeTransition}>
          <DialogTitle>Manage Sections</DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <Box bgcolor="background.default" p={2} borderBottom={1} borderColor="divider">
              <Typography variant="body2" color="text.secondary">
                Assign section masters to <b>{selectedClass?.className}</b>.
              </Typography>
            </Box>
            <List sx={{ pt: 0, maxHeight: 300, overflow: 'auto' }}>
              {getSectionOptionsForClass(selectedClass).length > 0 ? getSectionOptionsForClass(selectedClass).map((sec) => (
                <ListItem key={sec.sectionId} button onClick={() => setCheckedSections(prev => prev.includes(sec.sectionId) ? prev.filter(id => id !== sec.sectionId) : [...prev, sec.sectionId])}>
                  <Checkbox checked={checkedSections.includes(sec.sectionId)} color="primary" />
                  <ListItemText
                    primary={sec.sectionName}
                    secondary={sec.isLegacy ? "This section was deleted. Uncheck it to fully deassign it from this class." : null}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
              )) : <Typography p={3} color="text.secondary" align="center">No sections available.</Typography>}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, bgcolor: "background.default" }}>
            <Button onClick={() => setOpenSectionsDialog(false)} color="inherit">Cancel</Button>
            <Button onClick={handleSaveSections} variant="contained" disableElevation disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} /> : "Save Sections"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Class Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth TransitionComponent={FadeTransition}>
          <DialogTitle>Edit Class Info</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={3} pt={1}>
              <TextField label="Class Name" value={editClassName} onChange={(e) => setEditClassName(e.target.value)} required fullWidth />
              <TextField label="Room Number" value={editRoomNo} onChange={(e) => setEditRoomNo(e.target.value)} fullWidth />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setEditDialogOpen(false)} color="inherit">Cancel</Button>
            <Button onClick={handleSaveClassDetails} variant="contained" disableElevation disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} /> : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Class Dialog */}
        <Dialog open={newClassDialogOpen} onClose={() => setNewClassDialogOpen(false)} maxWidth="xs" fullWidth TransitionComponent={FadeTransition}>
          <DialogTitle>Create New Class</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={3} pt={1}>
              <TextField autoFocus label="Class Name" placeholder="e.g. Grade 10" value={newClassName} onChange={(e) => setNewClassName(e.target.value)} required fullWidth />
              <TextField label="Room Number" placeholder="e.g. 101A" value={newRoomNo} onChange={(e) => setNewRoomNo(e.target.value)} fullWidth />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setNewClassDialogOpen(false)} color="inherit">Cancel</Button>
            <Button onClick={handleCreateNewClass} variant="contained" disableElevation disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} /> : "Create Class"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Teacher Dialog */}
        <Dialog open={assignTeacherDialogOpen} onClose={() => setAssignTeacherDialogOpen(false)} maxWidth="xs" fullWidth TransitionComponent={FadeTransition}>
          <DialogTitle>Assign Class Teacher</DialogTitle>
          <DialogContent dividers>
            <Box pt={1}>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Select a primary homeroom teacher for <b>{selectedClass?.className}</b>.
              </Typography>
              <FormControl fullWidth>
                <InputLabel sx={{ bgcolor: "background.paper", px: 0.5 }}>Select Teacher</InputLabel>
                <Select value={classTeacherId} onChange={(e) => setClassTeacherId(e.target.value)} label="Select Teacher" sx={{ bgcolor: "background.paper" }}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {teachers.map((t) => (
                    <MenuItem key={t.employeeId} value={t.employeeId}>{t.firstName} {t.lastName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setAssignTeacherDialogOpen(false)} color="inherit">Cancel</Button>
            <Button onClick={handleAssignTeacher} variant="contained" disableElevation disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} /> : "Assign Teacher"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Global Snackbar */}
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: "100%", color: "#fff", fontWeight: 500, borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

      </PageContainer>
    </ThemeProvider>
  );
};

export default ClassManagement;
